import { useT } from '@kmgestao/i18n';
import { PageSectionCard, Skeleton, Switch, toast } from '@kmgestao/ui';

import {
  useClinicModules,
  useToggleClinicModule,
  type ClinicModuleRow,
} from '../../../../features/ai-clinic/index.js';

export interface ModulesCardProps {
  clinicId: string;
}

export function ModulesCard({ clinicId }: ModulesCardProps) {
  const { t } = useT('ai-clinic');
  const { data, isLoading, isError, error } = useClinicModules(clinicId);

  return (
    <PageSectionCard
      title={t('modules.title')}
      description={t('modules.description')}
      contentClassName="space-y-3"
    >
      {isLoading ? (
        <ul className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <li
              key={i}
              className="flex items-center gap-4 rounded-xl border border-border/70 bg-muted/20 p-4"
            >
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-2/3" />
              </div>
              <Skeleton className="h-6 w-11 rounded-full" />
            </li>
          ))}
        </ul>
      ) : isError ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Erro ao carregar módulos.'}
        </p>
      ) : !data || data.length === 0 ? (
        <p className="text-base text-muted-foreground">{t('modules.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {data.map((m) => (
            <ModuleRow key={m.id} clinicId={clinicId} module={m} />
          ))}
        </ul>
      )}
    </PageSectionCard>
  );
}

function ModuleRow({ clinicId, module }: { clinicId: string; module: ClinicModuleRow }) {
  const { t } = useT('ai-clinic');
  const toggle = useToggleClinicModule();

  const labelKey = `modules.keys.${module.moduleKey}` as const;
  const translatedLabel = t(labelKey, { defaultValue: module.namePtBr });

  const handleToggle = async (next: boolean) => {
    try {
      await toggle.mutateAsync({
        clinicId,
        moduleKey: module.moduleKey,
        enabled: next,
      });
      toast.success(next ? t('modules.success.enabled') : t('modules.success.disabled'));
    } catch (err) {
      toast.error(t('modules.error'), {
        description: err instanceof Error ? err.message : '',
      });
    }
  };

  return (
    <li className="flex flex-col gap-3 rounded-xl border border-border/70 bg-muted/20 p-4 sm:flex-row sm:items-center sm:gap-4">
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-base font-medium">{translatedLabel}</p>
        {module.description ? (
          <p className="text-sm leading-6 text-muted-foreground">{module.description}</p>
        ) : null}
        {!module.enabled && module.disabledReason ? (
          <p className="text-sm italic leading-6 text-muted-foreground">
            “{module.disabledReason}”
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-3 sm:shrink-0">
        <span
          className={
            'text-sm font-medium ' + (module.enabled ? 'text-success' : 'text-muted-foreground')
          }
        >
          {module.enabled ? t('modules.toggleOn') : t('modules.toggleOff')}
        </span>
        <Switch
          checked={module.enabled}
          onCheckedChange={handleToggle}
          disabled={toggle.isPending}
          srLabel={translatedLabel}
        />
      </div>
    </li>
  );
}
